import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CardUserComponent } from '@components/users/card-user/card-user.component';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from '@app/material.module';
import { inject, OnInit } from '@angular/core';
import { TechnicianApiService } from '@services/api/user/technician/technician-api.service';
import { User } from '@interfaces/user';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { ProfileComponent} from '@pages/users/technicians/profile/profile.component';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrServices } from '@services/toastr/toastr.service';
import { Technician } from '@interfaces/technician';
import { AuthService } from '@services/api/auth/auth.service';
import { Roles } from '@enums/Roles';
import { LoaderService } from '@services/loader/loader.service';

@Component({
  selector: 'app-technicians',
  imports: [CardUserComponent, TablerIconsModule, MaterialModule, CommonModule, ReactiveFormsModule, TranslateModule],
  standalone: true,
  templateUrl: './technicians.component.html',
  styleUrl: './technicians.component.scss'
})
export class TechniciansComponent implements OnInit{
  private technician_api_service = inject(TechnicianApiService);
  private toastr_service = inject(ToastrServices);
  private loadingService: LoaderService = inject(LoaderService);
  private user_api_service = inject(AuthService);
  protected user: User | null = null;
  protected dialog = inject(MatDialog);
  protected technicians: Technician[] = [];
  protected filteredTechnicians: Technician[] = [];
  private destroy$ = new Subject<void>();
  private filterInitialized = false;
  searchControl = new FormControl('');

  ngOnInit(): void {
    this.loadTechnicians();
    this.getUserData();
  }

  getUserData(): void {
    this.user_api_service.get_user_data()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.user = response.data;
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  loadTechnicians(): void {
    this.technician_api_service.get_technicians_list_data()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const technicians = response.data ?? [];
          this.setTechniciansData(technicians);
        },
        error: (error) => {
          console.log(error);
          this.setTechniciansData([]);
        }
      });
  }

  setupFilter(): void {
    if (this.filterInitialized) {
      return;
    }
    this.filterInitialized = true;

    this.searchControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(),
        tap(searchTerm => {
          const shouldFilter = !!searchTerm && searchTerm.length >= 3;
          if (shouldFilter) {
            this.loadingService.show();
          }
          this.filteredTechnicians = this.filterTechnicians(searchTerm);
          this.loadingService.hide();
        })
      )
      .subscribe();
  }

  addTechnician(obj: any): void{
    const dialogRef = this.dialog.open(ProfileComponent, {
      autoFocus: false,
      width: '50vw',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.toastr_service.showSuccess('Technician added succesfully', 'Success');
        this.loadTechnicians();
      }
    }, (error) => {
      console.log(error);
      this.toastr_service.showError('Impossible to add Technician', 'Error');
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected readonly Roles = Roles;

  private setTechniciansData(technicians: Technician[]): void {
    this.technicians = technicians;
    this.filteredTechnicians = this.filterTechnicians(this.searchControl.value);
    this.setupFilter();
  }

  private filterTechnicians(searchTerm: string | null | undefined): Technician[] {
    if (!this.technicians.length) {
      return [];
    }

    if (!searchTerm || searchTerm.length < 3) {
      return [...this.technicians];
    }

    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return this.technicians.filter(technician => {
      return (
        (technician.email && technician.email.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (technician.name && technician.name.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (technician.lastname && technician.lastname.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (technician.company && technician.company.name && technician.company.name.toString().toLowerCase().includes(lowerCaseSearchTerm))
      );
    });
  }


}
